import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Lock, Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabaseClient";

function ShieldLogo({ className = "h-10 w-10" }) {
    return (
        <svg viewBox="0 0 40 44" className={className} fill="none" aria-hidden="true">
            <defs>
                <linearGradient id="rpShield" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0EA5A4" />
                    <stop offset="100%" stopColor="#0F766E" />
                </linearGradient>
            </defs>
            <path
                d="M20 2 L36 8 V20 C36 30 28 38 20 42 C12 38 4 30 4 20 V8 Z"
                fill="url(#rpShield)"
                stroke="#D4AF37"
                strokeWidth="1.4"
            />
            <path
                d="M11 18 Q15 14 20 18 T29 18 M11 23 Q15 19 20 23 T29 23 M11 28 Q15 24 20 28 T29 28"
                stroke="#D4AF37"
                strokeWidth="1.8"
                strokeLinecap="round"
                fill="none"
            />
        </svg>
    );
}

export default function ResetPassword() {
    const { updatePassword } = useAuth();
    const navigate = useNavigate();
    const [ready, setReady] = useState(false);
    const [password, setPassword] = useState("");
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // After clicking the reset link, Supabase parses tokens from the URL
        // and fires PASSWORD_RECOVERY (or SIGNED_IN). Until then we wait.
        const { data } = supabase.auth.onAuthStateChange((event) => {
            if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") {
                setReady(true);
            }
        });
        // also handle the case where the listener has already fired
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (session) setReady(true);
        });
        return () => data.subscription.unsubscribe();
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        setLoading(true);
        try {
            await updatePassword(password);
            toast.success("Password updated — you&apos;re logged in.");
            navigate("/dashboard", { replace: true });
        } catch (err) {
            toast.error(err.message || "Could not update password");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#071A2F] text-white flex flex-col">
            <div className="absolute inset-0 mesh-hero opacity-40 pointer-events-none" />
            <div className="absolute inset-0 grid-pattern-dark opacity-30 pointer-events-none" />

            <Link
                to="/"
                className="relative flex items-center gap-2.5 p-6 sm:p-10 lg:p-14 z-10"
            >
                <ShieldLogo />
                <span className="font-bold text-lg">
                    TenderTrack<span className="gradient-text-teal">AI</span>
                </span>
            </Link>

            <div className="relative flex-1 flex items-center justify-center px-6 pb-10 z-10">
                <div className="w-full max-w-md">
                    <div className="badge-pill border border-gold/30 bg-gold/5 text-gold">
                        <Lock className="h-3 w-3" /> Set new password
                    </div>
                    <h1 className="mt-6 font-serif text-4xl lg:text-5xl font-bold uppercase tracking-tight leading-[1.05]">
                        Choose a <span className="gradient-text-teal">new password</span>
                    </h1>
                    <p className="mt-3 text-slate-400">
                        At least 8 characters. You&apos;ll be logged in automatically.
                    </p>

                    {!ready ? (
                        <p className="mt-8 text-sm text-slate-500 font-mono animate-pulse">
                            Verifying reset link…
                        </p>
                    ) : (
                        <form onSubmit={submit} className="mt-8 space-y-5">
                            <div>
                                <Label
                                    htmlFor="password"
                                    className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-300"
                                >
                                    New password
                                </Label>
                                <div className="relative mt-2">
                                    <Input
                                        id="password"
                                        type={show ? "text" : "password"}
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="At least 8 characters"
                                        className="h-12 rounded-xl bg-white/5 border-white/15 placeholder:text-slate-500 text-white focus-visible:ring-teal-500/40 focus-visible:border-teal-500/40 pr-11"
                                        data-testid="reset-password-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShow((s) => !s)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                                        aria-label="Toggle visibility"
                                    >
                                        {show ? (
                                            <EyeOff className="h-4 w-4" />
                                        ) : (
                                            <Eye className="h-4 w-4" />
                                        )}
                                    </button>
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="luxury-btn-gold w-full h-12 rounded-2xl text-sm font-bold uppercase tracking-[0.14em] inline-flex items-center justify-center gap-2 disabled:opacity-70"
                                data-testid="reset-submit-btn"
                            >
                                {loading ? "Updating…" : "Update Password"}
                                <ArrowRight className="h-4 w-4" />
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
